import { Group, SegmentedControl, Text, Title } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useState } from 'react';
import { CrudSection } from '../components/CrudSection';
import { EntityLabel } from '../components/EntityField';
import type { EntityKey } from '../lib/entities';
import { enumLabel, humanize } from '../lib/enums';
import {
  formatDate,
  formatDateTime,
  formatFxRate,
  formatHours,
  formatMoney,
  formatMonth,
  formatRateBp,
} from '../lib/format';

/**
 * Actions are stored as stable identifiers (`suppliers.payment.record`) because they are matched
 * on, counted and kept forever. Nobody should have to read one: the last part is the verb, the
 * parts before it are the subject, and that is enough to turn all of them into a sentence without
 * a seventy-line lookup table that would go stale the day a new action is added.
 */
const VERBS: Record<string, string> = {
  create: 'added',
  enroll: 'added',
  update: 'changed',
  upsert: 'saved',
  delete: 'removed',
  publish: 'published',
  assign: 'assigned',
  attach: 'linked',
  detach: 'unlinked',
  replace: 'replaced',
  reset: 'reset',
  post: 'posted',
  submit: 'submitted',
  approve: 'approved',
  build: 'built',
  close: 'closed',
  reopen: 'reopened',
  generate: 'built',
  reconcile: 'reconciled',
  pay: 'paid',
  record: 'recorded',
  issue: 'issued',
  adjust: 'corrected',
  transfer: 'moved',
  reposition: 'moved',
  eightySix: 'marked sold out',
  // An allergen is put on or taken off a dish. "Allergen flag" was not a sentence at all.
  flag: 'added',
  unflag: 'removed',
  grant: 'given',
  revoke: 'taken away',
  // Only the manager's queue writes this one, and "reviewed" beside "Refused action" reads as
  // though the office approved something. It did not — acknowledging changes nothing about the
  // action, which is the one thing that screen must not be ambiguous about anywhere it appears.
  reviewed: 'dealt with',
  /*
   * THIRTY MORE, COUNTED OUT OF THE BACKEND RATHER THAN GUESSED. 2026-07-29.
   *
   * A verb that is not in this map does not fall back to anything — it falls THROUGH, and the
   * stored identifier goes on the owner's screen. Measured on the real log: "Filed month file" and
   * "Delivery correct". Grepping every `action:` string in `backend/src` and checking each last
   * segment against this map found thirty with no word, and two of them are this week's headline
   * features: taking a supplier payment back read "Supplier payment reverse", and the second
   * signature on a bank deposit read "Bank deposit verify".
   *
   * They are all here now, whether an office screen or a register writes them, because the Activity log
   * says in its own first line that it shows all three sources.
   */
  add: 'added',
  apply: 'applied',
  // A ticket is bumped when the kitchen has finished it, and recalled when it should not have been.
  bump: 'finished',
  recall: 'sent back',
  comp: 'put on the house',
  compute: 'worked out',
  correct: 'corrected',
  count: 'counted',
  edit: 'changed',
  file: 'filed',
  fire: 'sent to the kitchen',
  import: 'imported',
  // `order.check.lines` — lines moved between the checks a table has been split into.
  lines: 'lines moved',
  merge: 'merged',
  open: 'opened',
  receive: 'received',
  refund: 'refunded',
  rematch: 'matched again',
  remove: 'removed',
  reorder: 'put in a new order',
  resolve: 'sorted out',
  // The two undo doors built this week. "Reverse" is our word; his is taking it back.
  reverse: 'taken back',
  seat: 'seated',
  split: 'split',
  take: 'taken',
  upload: 'filed',
  // Not "documented": what happened is that the photograph of a paper bill was put on the record.
  document: 'photograph attached',
  // The two-person check, on a deposit and on a drawer count alike.
  verify: 'confirmed',
  void: 'voided',
  waste: 'written off',
};

/**
 * What the thing is called by the people who run the bar, keyed by the whole subject path.
 *
 * Left to the automatic rule these came out as our table names: "Variant changed" for a size,
 * "Modifier option added" for a choice on a dish, "Bundle" for a combo, "Spot" for a place a guest
 * sits, and — worst — "Group added" for a new tax set, where the automatic rule had already
 * dropped the only word that said what kind of group it was.
 *
 * Keyed by path rather than by the last word so that `catalog.item` and `inventory.item` can be
 * told apart: one is a dish on the menu, the other is a case of beer in the bodega.
 */
const SUBJECTS: Record<string, string> = {
  catalog: 'Menu',
  'catalog.item': 'Menu item',
  'catalog.variant': 'Size',
  'catalog.modifierGroup': 'Choice set',
  'catalog.modifierOption': 'Choice',
  'catalog.itemModifierGroup': 'Choices on a menu item',
  'catalog.bundle': 'Combo',
  'catalog.bundle.slots': 'Combo contents',
  // Every entry here has to survive having a verb put after it. "When something is served" is a
  // fine description and "When something is served changed" is not a sentence, which is the same
  // failure as printing the table name — the row stops being readable.
  'catalog.availability': 'Serving times',
  'catalog.allergen': 'Allergen',
  inventory: 'Stock',
  'inventory.item': 'Stock item',
  'inventory.waste': 'Waste',
  'floor.spot': 'Place',
  'floor.zone': 'Area',
  'fiscal.emissionPoint': 'Invoice number range',
  'tax.group': 'Tax set',
  'tax.rate': 'Tax rate',
  'operations.register': 'Register',
  // Left to the automatic rule this read "Rejected mutation reviewed" — our word for it, on the
  // one screen whose whole job is answering "who did what" in the owner's.
  'operations.rejectedMutation': 'Refused action',
  'payroll.run': 'Payroll register',
  'payroll.timeclock': 'Time clock',
  'payroll.statutory': 'Deduction rule',
  'pricing.priceEntry': 'Price',
  'staff.pin': 'PIN',
  'staff.role.permission': 'Permission',
  'payroll.compensation': 'Pay rate',
  'suppliers.po': 'Purchase order',
  'suppliers.catalogItem': 'Supplier product',
  // "Receipt" means two different things one line apart in this log: goods arriving, and the
  // photographed paperwork for a ferry crossing. Both get the word the screens use.
  'suppliers.receipt': 'Delivery',
  'suppliers.freightCharge': 'Shipping charge',
  'suppliers.freightCharge.receipts': 'Shipping charge paperwork',
  'channels.itemMapping': 'Delivery menu link',
};

const singular = (word: string) => (word.endsWith('s') && !word.endsWith('ss') ? word.slice(0, -1) : word);

/**
 * The assistant's own rows do not fit the `area.subject.verb` shape the rest of the log uses, and
 * fed through the general rule they came out as "Mcp confirm" and "Tool list suppliers" — our
 * internal vocabulary, printed on the one screen that is meant to answer "who changed what".
 * They are also the most-read rows here, because every lookup an assistant does writes one.
 */
function describeAssistantAction(parts: string[]): string | null {
  const [, second, ...rest] = parts;
  if (second === 'tool') {
    const tool = rest.join(' ');
    if (tool.startsWith('upload')) return 'Filed a photo of a document';
    const read = /^(list|query|get|match)[\s_]*/.exec(tool);
    if (!read) return null;
    // humanize gives sentence case; only the leading capital goes, and only when the word is not
    // an acronym the registry insists on keeping — "FX rate", not "fx rate".
    const phrase = humanize(tool.slice(read[0].length));
    if (!phrase) return 'Looked something up';
    const noun = /^[A-Z]{2,}/.test(phrase) ? phrase : phrase.charAt(0).toLowerCase() + phrase.slice(1);
    return `Looked up ${noun}`;
  }
  if (second === 'propose') return 'Asked to make a change';
  if (second === 'confirm') return 'Change confirmed and applied';
  if (second === 'reject') return 'Change turned down';
  return null;
}

function describeAction(action: string): string {
  const parts = action.split('.');
  if (parts.length < 2) return humanize(action);
  if (parts[0] === 'mcp') {
    const said = describeAssistantAction(parts);
    if (said) return said;
  }
  const verb = parts[parts.length - 1] ?? '';
  const rest = parts.slice(0, -1);
  // The name the bar uses wins. Only when there is none does the first part get dropped — with
  // three or more parts it is the corner of the system, and "Suppliers payment recorded" is not
  // how anyone says "Payment recorded".
  const named = SUBJECTS[rest.join('.')];
  const subject = rest.length > 1 ? rest.slice(1) : rest;
  const head =
    named ?? (subject.length === 1 ? humanize(singular(subject[0] as string)) : humanize(subject.join(' ')));
  return `${head} ${VERBS[verb] ?? humanize(verb).toLowerCase()}`;
}

/**
 * The log keeps the name the actor had at the time, which is what should be shown — it stays
 * right after somebody is renamed. Only when there is no name does it fall back to looking the
 * stored identifier up.
 */
function ActorCell({ row }: { row: Record<string, unknown> }) {
  const name = row.actorName;
  const from = enumLabel('actorType', row.actorType);
  const who =
    typeof name === 'string' && name.trim() ? (
      <Text size="sm">{name}</Text>
    ) : row.actorType === 'staff' && row.actorId ? (
      <EntityLabel entity="staff" id={row.actorId} />
    ) : row.deviceId ? (
      <EntityLabel entity="device" id={row.deviceId} />
    ) : (
      <Text c="dimmed">—</Text>
    );
  return (
    <div>
      {who}
      {/* Where it came from used to be its own column, which meant a row read "Dan Siegel |
          Office". It is a qualifier on the name, not a second fact, and folding it in buys back a
          column for something that answers a question. */}
      <Text size="xs" c="dimmed">
        {from}
      </Text>
    </div>
  );
}

/**
 * Which record a row is about, for the kinds the name registry can look up.
 *
 * "Price changed" fourteen times in an afternoon is not an answer to "who changed what" — the
 * column that used to sit here repeated the KIND of thing ("Menu item") beside a sentence that had
 * just said it, and never once said which one. Kinds with no list to resolve against fall back to
 * that kind, which is no worse than what was there before.
 */
const RECORD_ENTITY: Record<string, EntityKey> = {
  staff: 'staff',
  menuItem: 'menuItem',
  category: 'category',
  variant: 'variant',
  modifierOption: 'modifierOption',
  bundle: 'bundle',
  recipe: 'recipe',
  inventoryItem: 'inventoryItem',
  supplier: 'supplier',
  salesChannel: 'channel',
  floorZone: 'zone',
  device: 'device',
  taxGroup: 'taxGroup',
  priceList: 'priceList',
  commissionPlan: 'commissionPlan',
  tourOperatorProfile: 'referralProfile',
};

/**
 * The kinds with no list to look a name up in, in the words the bar uses.
 *
 * Falling straight through to the automatic rule printed our table names in this column even
 * though the sentence beside it had been fixed: "Item modifier group", "Fiscal emission point",
 * "Statutory param", "Mcp settings" — for a screen the owner opens "Assistant access". Anything
 * not listed still reads acceptably on its own ("Bank account", "Purchase order").
 */
const RECORD_KINDS: Record<string, string> = {
  allergenFlag: 'Allergen',
  availabilitySchedule: 'Serving times',
  catalogVersion: 'Published menu',
  compensation: 'Pay rate',
  fiscalEmissionPoint: 'Invoice number range',
  fiscalDocument: 'Invoice',
  floorSpot: 'Place a guest sits',
  freightCharge: 'Shipping charge',
  itemModifierGroup: 'Choices on a menu item',
  mcpSettings: 'Assistant access',
  modifierGroup: 'Choice set',
  // A row in the log of what registers have sent us. "Mutation log" is the table's name, not a thing
  // anybody at this bar has ever called anything.
  mutationLog: 'Something a register sent',
  platformItemMapping: 'Delivery menu link',
  platformSettlement: 'Delivery platform payout',
  priceEntry: 'Price',
  receipt: 'Delivery',
  register: 'Register',
  statutoryParam: 'Deduction rule',
  supplierCatalogItem: 'Supplier product',
  timeClockEvent: 'Time clock entry',
  wasteEntry: 'Waste',
};

function RecordCell({ row }: { row: Record<string, unknown> }) {
  const type = typeof row.entityType === 'string' ? row.entityType : '';
  const entity = RECORD_ENTITY[type];
  if (entity && row.entityId) return <EntityLabel entity={entity} id={row.entityId} />;
  if (!type) return <Text c="dimmed">—</Text>;
  return (
    <Text size="sm" c="dimmed">
      {RECORD_KINDS[type] ?? humanize(type)}
    </Text>
  );
}

/** A stored identifier: 26 characters of Crockford base32, and meaningless to everyone. */
const ULID = /^[0-9A-HJKMNP-TV-Z]{26}$/i;

/** The three ways this schema names a stored key: `supplierId`, `clientUlid`, `userOid`, and `id`. */
const IDENTIFIER_KEY = /^id$|[a-z0-9](?:Id|Ulid|Oid)$/;

/**
 * One fact out of the stored detail, in the units a person reads.
 *
 * The detail column is whatever the handler chose to record, so it arrived on screen raw: a
 * settlement mismatch printed "Expected net: 1234567" for L 12,345.67, a rate change printed
 * "Rate micro: 26700000" for 26.7, and a role change printed a JSON array of ULIDs. Formatting by
 * the SUFFIX the whole codebase already stores these in — Minor, Micro, Bp — fixes every one of
 * them at once, including the handlers written after this.
 */
/**
 * Detail keys whose value is one of the platform's own enum members.
 *
 * A stored detail is a plain record, so a value like `shortDelivery` arrived here as a string and
 * went out through `String(value)` — printing **"Discrepancy reason: shortDelivery"** on the
 * owner's Activity log, which is precisely the artifact `scripts/check-raw-values.mjs` exists to
 * stop and could not see, because it reads columns rather than a JSON blob written at runtime.
 *
 * Named here rather than guessed, because the registered wording is worth reaching for: this one
 * value is written up as *"They billed for goods that did not arrive"*, which is the sentence
 * somebody is looking for when they open this page.
 *
 * Anything NOT in this map still cannot come out raw — the fallback below humanises any value that
 * is plainly an identifier rather than English, so the next handler to record an enum reads
 * "Short delivery" at worst, never `shortDelivery`.
 */
const DETAIL_ENUMS = {
  discrepancyReason: 'billDiscrepancyReason',
} as const;

/** A stored `2026-07-29`, and a stored `2026-06`. Both reach this page from real handlers. */
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const ISO_MONTH = /^\d{4}-\d{2}$/;
/**
 * A stored INSTANT — `2026-07-29T13:22:12.352Z`. The third shape, and the only one that was wrong
 * rather than ugly.
 *
 * Measured on the real log: signing a pay register off and then returning it to draft records the
 * moment it was signed, and the row printed **"Was finalized at: 2026-07-29T13:22:12.352Z"** in the
 * Details column while the When column on the SAME ROW read "Jul 29, 2026, 7:22 AM". Two times for
 * one event, six hours apart, because one went through a formatter and one did not — and the raw
 * one is in UTC, which on this island is the following morning for anything after six in the
 * evening. That is wrong number 3's shape, on the screen somebody opens to settle an argument.
 *
 * The day and month branches below match anchored, so they never saw this: `2026-07-29T…` is not
 * `^\d{4}-\d{2}-\d{2}$`. Nothing else had to change for a handler to put an instant here.
 */
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** A value that is an identifier rather than a sentence: `shortDelivery`, `bank_transfer`, `HNL`. */
const NOT_ENGLISH = /^[a-z]+(?:[A-Z][a-z0-9]*)+$|^[a-z0-9]+(?:_[a-z0-9]+)+$/;

/**
 * An amount whose key already says which money it is: `usdMinor`, `hnlMinor`.
 *
 * The `currency` companion below only works for a record holding ONE amount. Anything that moves
 * both piles at once cannot use it and stores a key per currency instead — and that came out as
 * **"USD: 250.00 · HNL: 0.00"** on a bank deposit: an ISO code as a label on an English screen, and
 * an unsymbolled figure beside it, which is the same defect the `currency` companion was written to
 * remove and one shape short of it. The code IS the currency, so it goes into the symbol rather
 * than in front of the number twice.
 */
const CURRENCY_KEYED_AMOUNT = /^(usd|hnl)Minor$/;

function describeDetailValue(
  key: string,
  value: unknown,
  /**
   * The money this record is in, when the handler stored it beside the amount.
   *
   * It very often did, and this cell used to print the two as separate facts — "Amount: 4,800.00 ·
   * Currency: HNL" — which puts a three-letter code on the owner's screen and leaves him to pair
   * it with the number himself. `L 4,800.00` is one fact and it is the one he came for.
   */
  currency: string | null,
): { label: string | null; shown: string } | null {
  if (value === null || value === undefined || value === '') return null;

  // A list of identifiers — the roles somebody was given, the records a job touched. How many is
  // the readable part; the ids themselves are not.
  if (Array.isArray(value)) {
    if (value.length === 0 || !value.every((v) => typeof v === 'string')) return null;
    return { label: humanize(key.replace(/Ids$/, 's')), shown: String(value.length) };
  }
  // Identifiers name nothing on screen, and neither do the nested payloads some handlers record.
  // What was touched is already said in the sentence beside this.
  //
  // Case matters. Matching "id" case-insensitively also swallows any word ENDING in those two
  // letters — `breakPaid` on a shift is a real column, and dropping "the break was paid" out of
  // the record of a schedule change because it rhymes with an id is the silent kind of wrong.
  if (IDENTIFIER_KEY.test(key) || typeof value === 'object') return null;
  // Facts about a FILE, not about the business: filing a photograph of a propane bill printed
  // "Content type: image/png · Size bytes: 3932" on the owner's log. Neither is a thing he can act
  // on, and neither is in his language. What the photograph was of stays.
  if (key === 'contentType' || key === 'sizeBytes') return null;
  if (typeof value === 'string' && ULID.test(value)) return null;

  if (typeof value === 'number') {
    // The key names the money: print it in that money and drop the label, because "USD: $ 250.00"
    // says dollars twice and the symbol is the half a person reads.
    const keyed = CURRENCY_KEYED_AMOUNT.exec(key);
    if (keyed) return { label: null, shown: formatMoney(value, keyed[1]!.toUpperCase()) };
    // Symbolled when the record says which money it is, bare when it does not — an amount labelled
    // with the WRONG currency is worse than an unlabelled one, and 12,345.67 is at least the right
    // number. `formatMoney` with an empty currency is the established way to say "no symbol".
    if (key.endsWith('Minor'))
      return { label: humanize(key.slice(0, -5)), shown: formatMoney(value, currency ?? '') };
    if (key.endsWith('Micro')) return { label: humanize(key.slice(0, -5)), shown: formatFxRate(value) };
    if (key.endsWith('Bp')) return { label: humanize(key.slice(0, -2)), shown: formatRateBp(value) };
    // Reads as a sentence rather than a table cell, so zero is spelled out: "Overtime: none" is
    // how somebody would say it, where "Overtime: 0h" is how a column would print it.
    if (key.endsWith('Minutes'))
      return { label: humanize(key.slice(0, -7)), shown: value === 0 ? 'none' : formatHours(value) };
  }

  if (typeof value === 'string') {
    const label = humanize(key);
    /*
     * A STORED VALUE IS NOT ENGLISH UNTIL SOMEBODY MAKES IT SO, and three kinds of it were reaching
     * this page raw. Measured on the real log, 2026-07-29:
     *
     *   "Received total: 108.00 · Billed total: 144.00 · Discrepancy reason: shortDelivery"
     *   "Business date: 2026-07-29 · Reason: …"      "Month: 2026-06 · Filed on: 2026-07-29"
     *
     * A registered enum gets its registered sentence; a date gets the format every other screen in
     * the office uses; anything else that is plainly an identifier rather than a phrase is at least
     * humanised, so a handler written next year cannot put a bare `camelCase` literal on the
     * owner's screen by doing nothing wrong.
     */
    const group = DETAIL_ENUMS[key as keyof typeof DETAIL_ENUMS];
    if (group) return { label, shown: enumLabel(group, value) };
    if (ISO_DAY.test(value)) return { label, shown: formatDate(value) };
    if (ISO_MONTH.test(value)) return { label, shown: formatMonth(value) };
    if (ISO_INSTANT.test(value)) return { label, shown: formatDateTime(value) };
    if (NOT_ENGLISH.test(value)) return { label, shown: humanize(value) };
    return { label, shown: value };
  }

  const shown = typeof value === 'boolean' ? (value ? 'yes' : 'no') : String(value);
  return { label: humanize(key), shown };
}

function DetailCell({ detail }: { detail: unknown }) {
  if (!detail || typeof detail !== 'object') return null;
  const record = detail as Record<string, unknown>;
  // Read once and taken off the list: it is a property OF the amounts beside it, not a fact of its
  // own, and "Currency: HNL" is a raw code on an English screen either way.
  const currency = typeof record.currency === 'string' ? record.currency : null;
  const parts = Object.entries(record)
    .filter(([key]) => !(currency && key === 'currency'))
    .map(([key, value]) => describeDetailValue(key, value, currency))
    .filter((p): p is { label: string | null; shown: string } => p !== null)
    .map((p) => (p.label ? `${p.label}: ${p.shown}` : p.shown));
  if (!parts.length) return null;
  return (
    <Text size="xs" c="dimmed" lineClamp={2}>
      {parts.join(' · ')}
    </Text>
  );
}

/** A day picked here is a local day; the log records the exact instant, in UTC. */
const dayStart = (date: string | null) => (date ? new Date(`${date}T00:00:00`).toISOString() : undefined);
const dayEnd = (date: string | null) => (date ? new Date(`${date}T23:59:59.999`).toISOString() : undefined);

export function AuditPage() {
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  // Defaults to changes: an assistant writes a row every time it merely LOOKS something up, and a
  // busy conversation pushes a day's real changes off the end of the list before it is even sent.
  const [include, setInclude] = useState('changes');

  return (
    <>
      <Title order={3} mb="md">
        Activity log
      </Title>
      <CrudSection
        title="What has been done"
        description="Every change anyone has made — in the office, on a register, or by the assistant. Nothing here can be edited or deleted."
        listPath="/admin/v1/admin/audit-events"
        listQuery={{ from: dayStart(from), to: dayEnd(to), include, limit: 200 }}
        emptyMessage={
          include === 'changes' ? 'Nothing was changed in these dates.' : 'Nothing happened in these dates.'
        }
        actions={
          /*
           * Wraps on a narrow window: this row is a segmented control and two date boxes, and under
           * `nowrap` it could not fold, so the PAGE scrolled sideways — 619px inside 390px. A filter
           * bar drops to a second line; it does not take the page with it.
           */
          <Group gap="xs" align="flex-end">
            <SegmentedControl
              size="xs"
              value={include}
              onChange={setInclude}
              data={[
                { value: 'changes', label: 'Changes' },
                { value: 'all', label: 'Including lookups' },
              ]}
            />
            <DateInput
              label="From"
              value={from}
              onChange={setFrom}
              valueFormat="D MMM YYYY"
              placeholder="Any day"
              clearable
              size="xs"
              w={150}
              popoverProps={{ withinPortal: true }}
            />
            <DateInput
              label="To"
              value={to}
              onChange={setTo}
              valueFormat="D MMM YYYY"
              placeholder="Any day"
              clearable
              size="xs"
              w={150}
              popoverProps={{ withinPortal: true }}
            />
          </Group>
        }
        columns={[
          { accessor: 'at', title: 'When', unit: 'datetime', noWrap: true },
          { accessor: 'actorName', title: 'Who', render: (r) => <ActorCell row={r} /> },
          {
            accessor: 'action',
            title: 'What happened',
            render: (r) => describeAction(String(r.action ?? '')),
          },
          { accessor: 'entityType', title: 'Which record', render: (r) => <RecordCell row={r} /> },
          { accessor: 'detail', title: 'Details', render: (r) => <DetailCell detail={r.detail} /> },
        ]}
      />
    </>
  );
}
